import * as React from "react";
import { addDays, addYears, stripToDay } from "../../../../../../../../react-core/utils/date";
import { NumberTextBoxEditor } from "../../../../../../../../react-core/Html/NumberTextBoxEditor";
import { MoneyHelper } from "../../../../../../../Shared/Common/MoneyHelper";
import { JsonDateParser } from "../../../../../../../../react-core/Utils/JsonDateParser";
import { DatePicker } from "../../../../../../../../react-core/Datepicker/DatePicker";
import { datePickerOptions } from "../../../../Common/Resource/Contents";
import { PensionApply } from "../../../../../../../../Wrap/Models/Models/Processes/Apply/Pension/PensionApply";
import { InvalidateDate } from "../../../../../../../../react-core/Datepicker/Resources/ContentContent";
import {
    TransferOrDrawdown,
    nbSippIncomePaymentDetailsLabels,
    nbSippIncomePaymentDetailsMessages,
} from "../../../Resource/Contents";

import { IProcessComponentProps, ProcessComponent } from "../../../../../../../Shared/Process/Process/ProcessComponent";
import { DrawdownType } from "../../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { PensionArrangementType } from "../../../../../../../../Wrap/Constants/Enums/Domain/PensionArrangementType";
import { Lightbox, LightboxType } from "../../../../../../../../react-core/Html/Components/Lightbox";

interface ITransferTotalsProps extends IProcessComponentProps {
    data: PensionApply;
    cappedTransfers: Array<TransferOrDrawdown>;
    flexiTransfers: Array<TransferOrDrawdown>;
    onChange: (changes) => void;
}
interface ITransferTotalsState {
    showPopUp: boolean;
    frequencyFlexiIncomeReview: number;
}

export class TransferTotals extends ProcessComponent<ITransferTotalsProps, ITransferTotalsState> {
    constructor(props: any) {
        super(props);
        this.state = { showPopUp: false, frequencyFlexiIncomeReview: this.props.data?.DrawdownModel?.Income?.FrequencyFlexiIncomeReview != 0 ? this.props.data?.DrawdownModel?.Income?.FrequencyFlexiIncomeReview : 12 };
    }

    private handleChange(changes) {
        const { data, onChange } = this.props;
        const changedIncomeReview = { ...data, ...changes };
        this.onDataChange(onChange, changedIncomeReview, data, "Income");
    }

    private renderIncomeReviewDateWarningMessage() {
        const { showPopUp } = this.state;
        if (showPopUp) {
            return (
                <Lightbox
                    show={showPopUp}
                    title={"Warning"}
                    contentAfterTitle={null}
                    onClose={() => this.setState({ showPopUp: false })}
                    type={LightboxType.Modal}
                    cssWrapper="is-default-lightbox is-narrow"
                >
                    <p>{nbSippIncomePaymentDetailsMessages.nextFlexiReviewDateMoreThan3Year}</p>
                    <button className="btn primary" onClick={() => this.setState({ showPopUp: false })}>
                        OK
                    </button>
                </Lightbox>
            );
        }
    }
    
    private flexiTotals(): number {
        const { flexiTransfers } = this.props;
        return flexiTransfers.reduce((sum, transfer) => {
            const amount: number = transfer.IncomePaymentDetails.GrossIncomePerPayment?.Amount || 0;
            return sum + amount;
        }, 0);
    }

    private isShowFlexiIncome() {
        const { data, cappedTransfers, flexiTransfers } = this.props;

        // Specific arrangement = false
        if (data?.DrawdownModel?.Income?.TargetSpecificArrangements !== true) {
            // standard algorithm
            // need check if there's capped transfer, if yes, then hide flexi review section - false
            return cappedTransfers.length == 0;
        } else {
            // specific algorithm
            // need to check if income is paied from flexi arrangement, if yes, then show flexi review section - true
            let flag = false;
            flexiTransfers.forEach((v: TransferOrDrawdown) => {
                if (v?.IncomePaymentDetails?.GrossIncomePerPayment?.Amount > 0) {
                    flag = true;
                }
            });
            return flag;
        }
    }

    public render(): JSX.Element {
        const { data, onChange } = this.props;

        const yearFromNow = () => {
            const result = stripToDay(addYears(new Date(Date.now()), 1));
            if (result.getDate() > 28) {
                result.setDate(28);
            }
            return result;
        };

        if (this.isShowFlexiIncome() && 
            (!data?.DrawdownModel?.Income?.FlexiIncomeReviewDate || 
            data?.DrawdownModel?.Income?.FlexiIncomeReviewDate.toString() === InvalidateDate)) {
            
            const defaultDate = yearFromNow();

            if (data?.DrawdownModel?.Income) {
                data.DrawdownModel.Income.FlexiIncomeReviewDate = JsonDateParser.toJsonDateNoOffset(defaultDate);
            }
        }

        return (
            <>
                {this.isShowFlexiIncome() && (
                    <>
                        <h3>Flexible income</h3>
                        <div className="row">
                            <div className="col-4">
                                <DatePicker
                                    selected={
                                        data?.DrawdownModel?.Income?.FlexiIncomeReviewDate &&
                                        data?.DrawdownModel?.Income?.FlexiIncomeReviewDate.toString() != InvalidateDate
                                            ? JsonDateParser.toDateNoOffset(
                                                  data?.DrawdownModel?.Income?.FlexiIncomeReviewDate
                                              )
                                            : yearFromNow()
                                    }
                                    label={nbSippIncomePaymentDetailsLabels.nextFlexiReviewDate}
                                    {...datePickerOptions}
                                    alwaysValidateOnUpdate={true}
                                    moreThan={{
                                        errorMessage: nbSippIncomePaymentDetailsMessages.nextFlexiReviewDateUnder,
                                        value: stripToDay(addDays(new Date(Date.now()), 20)),
                                    }}
                                    filterDate={{
                                        errorMessage: nbSippIncomePaymentDetailsMessages.dateOver28,
                                        validator: (v) => v.getDate() <= 28,
                                    }}
                                    required={nbSippIncomePaymentDetailsMessages.nextFlexiReviewDateRequired}
                                    onChange={(v) => {
                                        if (v > addYears(new Date(), 3)) {
                                            this.setState({ showPopUp: true });
                                        }

                                        this.handleChange({
                                            ...data?.DrawdownModel?.Income,
                                            FlexiIncomeReviewDate: JsonDateParser.toJsonDateNoOffset(v)
                                        });
                                    }}
                                />
                            </div>
                        </div>
                        {this.renderIncomeReviewDateWarningMessage()}
                        <div className="row">
                            <div className="col-4">
                                <NumberTextBoxEditor
                                    value={
                                        this.state.frequencyFlexiIncomeReview
                                            ? this.state.frequencyFlexiIncomeReview
                                            : data.DrawdownModel?.Income?.FrequencyFlexiIncomeReview
                                    }
                                    moreThan={{
                                        errorMessage:
                                            nbSippIncomePaymentDetailsMessages.frequencyOfFlexiIncomeReviewUnderOver,
                                        value: 0,
                                    }}
                                    atMost={{
                                        errorMessage:
                                            nbSippIncomePaymentDetailsMessages.frequencyOfFlexiIncomeReviewUnderOver,
                                        value: 60,
                                    }}
                                    label={nbSippIncomePaymentDetailsLabels.frequencyOfFlexiIncomeReview}
                                    onChange={(v) => {
                                        this.setState({ frequencyFlexiIncomeReview: v });

                                        this.handleChange({
                                            ...data?.DrawdownModel?.Income,
                                            FrequencyFlexiIncomeReview: v
                                        });
                                    }}
                                    required={
                                        nbSippIncomePaymentDetailsMessages.frequencyOfFlexiIncomeFrequencyRequired
                                    }
                                />
                            </div>
                        </div>
                        <hr />
                    </>
                )}
            </>
        );
    }
}

export default TransferTotals;
